import { useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getApiBaseUrl } from '../../config/apiConfig';
import { warn } from '../../utils/logger';

const isBrowser = typeof window !== 'undefined';

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

const detectCurrentPage = () => {
  if (!isBrowser) return 'LandingPage';

  const pathname = window.location.pathname || '';
  
  // Mapa de rutas (incluye rutas antiguas HTML y nuevas de React Router)
  const map = new Map([
    // Rutas antiguas HTML
    ['/pages/ShopCursosPage/cursos.html', 'ShopCursosPage'],
    ['/pages/ShopEventosPage/eventos.html', 'ShopEventosPage'],
    ['/pages/ShopProductosPage/tienda.html', 'ShopProductosPage'],
    ['/pages/MisCursosPage/mis-cursos.html', 'MisCursosPage'],
    ['/pages/MisEventosPage/mis-eventos.html', 'MisEventosPage'],
    ['/pages/MisComprasPage/mis-compras.html', 'MisComprasPage'],
    ['/pages/LoginPage/login.html', 'LoginPage'],
    ['/pages/Register/register.html', 'RegisterPage'],
    ['/pages/AdminDashboardPage/admin-dashboard.html', 'AdminDashboardPage'],
    ['/pages/ShopCarritoPage/cart.html', 'ShopCarritoPage'],
    // Rutas de React Router
    ['/login', 'LoginPage'],
    ['/register', 'RegisterPage'],
    ['/admin', 'AdminDashboard'],
    ['/admin/dashboard', 'AdminDashboard'],
    ['/admin/courses', 'AdminCourses'],
    ['/admin/users', 'AdminUsuariosPage'],
    ['/admin/roles', 'AdminRolesPage'],
  ]);

  if (map.has(pathname)) {
    return map.get(pathname);
  }

  // Detectar rutas de React Router por patrón
  if (pathname.startsWith('/admin/')) {
    const route = pathname.split('/').pop() || 'AdminDashboard';
    return route.charAt(0).toUpperCase() + route.slice(1) + 'Page';
  }

  if (pathname === '/' || pathname === '/index.html') {
    return 'HomePage';
  }

  return pathname.split('/').pop()?.replace('.html', '') || 'LandingPage';
};

const UniversalTracker = () => {
  const { isAuthenticated, user, token } = useAuth();

  useEffect(() => {
    if (!isBrowser) return undefined;
    if (!isAuthenticated || !user?.id || !token) return undefined;

    const API_BASE = getApiBaseUrl();
    const currentPage = detectCurrentPage();
    let lastActivity = Date.now();
    let heartbeatTimer;
    let mounted = true;

    const buildPayload = (status = 'online') => ({
      userId: user.id,
      lastActivity: new Date(lastActivity).toISOString(),
      page: currentPage,
      status,
    });

    const sendHeartbeat = async (status = 'online') => {
      try {
        await fetch(`${API_BASE}/api/online/heartbeat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(buildPayload(status)),
          keepalive: status === 'offline',
        });
      } catch (error) {
        if (status !== 'offline') {
          warn('[UniversalTracker] Error enviando heartbeat', error);
        }
      }
    };

    const markActivity = () => {
      lastActivity = Date.now();
    };

    heartbeatTimer = window.setInterval(() => {
      if (!mounted) return;
      sendHeartbeat('online');
    }, 30000);

    ACTIVITY_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, markActivity, true);
    });

    const visibilityHandler = () => {
      if (document.hidden) {
        sendHeartbeat('offline');
      } else {
        markActivity();
        sendHeartbeat('online');
      }
    };

    document.addEventListener('visibilitychange', visibilityHandler);

    const beforeUnloadHandler = () => {
      const payload = JSON.stringify(buildPayload('offline'));
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${API_BASE}/api/online/heartbeat`, payload);
      } else {
        fetch(`${API_BASE}/api/online/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: payload,
          keepalive: true,
        }).catch(() => {});
      }
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);
    sendHeartbeat('online');

    return () => {
      mounted = false;
      if (heartbeatTimer) {
        window.clearInterval(heartbeatTimer);
      }
      ACTIVITY_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName, markActivity, true);
      });
      document.removeEventListener('visibilitychange', visibilityHandler);
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      sendHeartbeat('offline');
    };
  }, [isAuthenticated, token, user]);

  return null;
};

export default UniversalTracker;





